import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AppConfigService, GridBaseQuery, GridHelperService } from '@core';
import { KendoDataSourceResultDto } from '@shared';
import { CaseTypeResponseDto } from '../dtos/case-type-response.dto';
import { CaseTypeDetailResponseDto } from '../dtos/case-type-detail-response.dto';
import { CaseTypeCategoryResponseDto } from '../dtos/case-type-category-response.dto';
import { CreateCaseTypeRequestDto } from '../dtos/create-case-type-request.dto';
import { UpdateCaseTypeRequestDto } from '../dtos/update-case-type-request.dto';
import { ToggleCaseTypeActiveRequestDto } from '../dtos/toggle-case-type-active-request.dto';
import { CaseTypeDeletionImpactResponseDto } from '../dtos/case-type-deletion-impact-response.dto';

@Injectable({ providedIn: 'root' })
export class CaseTypeApi {
  private readonly _http = inject(HttpClient);
  private readonly _config = inject(AppConfigService);
  private readonly _gridHelper = inject(GridHelperService);
  private readonly _baseUrl = `${this._config.versionedApiUrl}/cases/case-types`;

  public getAllCaseTypes(): Observable<KendoDataSourceResultDto<CaseTypeResponseDto>> {
    return this._http.get<KendoDataSourceResultDto<CaseTypeResponseDto>>(this._baseUrl);
  }

  public getActiveCaseTypes(): Observable<CaseTypeResponseDto[]> {
    return this._http.get<CaseTypeResponseDto[]>(`${this._baseUrl}/active`);
  }

  public getCaseTypesWithPaging(query: GridBaseQuery): Observable<KendoDataSourceResultDto<CaseTypeResponseDto>> {
    const params = this._gridHelper.buildGridParams(query);
    return this._http.get<KendoDataSourceResultDto<CaseTypeResponseDto>>(this._baseUrl, { params });
  }

  public getCaseTypeById(id: string): Observable<CaseTypeDetailResponseDto> {
    return this._http.get<CaseTypeDetailResponseDto>(`${this._baseUrl}/${id}`);
  }

  public getCategories(): Observable<CaseTypeCategoryResponseDto[]> {
    return this._http.get<CaseTypeCategoryResponseDto[]>(`${this._baseUrl}/categories`);
  }

  public createCaseType(request: CreateCaseTypeRequestDto): Observable<{ id: string }> {
    return this._http.post<{ id: string }>(this._baseUrl, request);
  }

  public updateCaseType(id: string, request: UpdateCaseTypeRequestDto): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/${id}`, request);
  }

  public deleteCaseType(id: string): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/${id}`);
  }

  public toggleCaseTypeActive(id: string, request: ToggleCaseTypeActiveRequestDto): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/toggle-active`, request);
  }

  public getCaseTypeDeletionImpact(id: string): Observable<CaseTypeDeletionImpactResponseDto> {
    return this._http.get<CaseTypeDeletionImpactResponseDto>(`${this._baseUrl}/${id}/deletion-impact`);
  }
}
